import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import Datatable from "../../../core/common/dataTable";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import CommonSelect2 from "../../../core/common/commonSelect2";
import MultiSearchableSelect, {
  Option,
} from "../../../core/common/MultiSearchableSelect";
import { useNavigate } from "react-router";
import { actionOptions } from "../../track-case-program/WorkInProgress";
import { all_routes } from "../../router/all_routes";
import {
  parseDate,
  parseDaysString,
  parseNumber,
} from "../../../core/methods/sorterMethods";
import EditIcon from "../../../core/svg-icons/EditIcon";
import {
  data_applicantPending,
  data_candidatePendency,
  data_WIP,
} from "./data_columns";
import TimelineModal from "../../track-case-program/TimelineModal";
import ModifyInvite from "../../track-case-program/applicant-invited/ModifyInviteModal";
import useApi from "../../../core/customhook/useApi";
import { useAuth } from "../../../core/context/useAuthContext";
import webservice_url from "../../../core/webservice/Webservice_url";
import "./reports-breakdown.scss";
import dayjs from "dayjs";

interface CustomerScoreFilterState {
  program: string[];
  sbu: string[];
}

const CustomerScoreCard = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const orgId = auth?.orgId;

  const [selectedTableData, setSelectedTableData] = useState<{
    type: string;
    value: number | string;
  } | null>(null);

  const [closedCaseList, setClosedCaseList] = useState<any[]>([]);

  const [customerScoreData, setCustomerScoreData] =
    useState<CustomerScoreFilterState>({
      program: [],
      sbu: [],
    });

  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);

  const [projects, setProjects] = useState<Option[]>([]);
  const [programOptions, setProgramOptions] = useState<Option[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingProgram, setLoadingProgram] = useState(false);

  const selectedProjectIds = customerScoreData.program;
  const selectedProgramIds = customerScoreData.sbu;
  const projectIdForProgramApi = selectedProjectIds[0] || "";

  const getProjects = useApi<any>(
    `/api/v1/master/organizations/${orgId}/projects`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getProgram = useApi<any>(
    projectIdForProgramApi
      ? `/api/v1/master/projects/${projectIdForProgramApi}/programs`
      : "",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getMonthsReport = useApi<any>(webservice_url.dashboardMonthsReportAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const fetchProjects = async () => {
    if (!orgId) return;

    setLoadingProjects(true);
    try {
      const res = await getProjects.callApi();
      if (res?.success) {
        const projectOptions = (res.data || []).map((item: any) => ({
          value: item.project_id,
          label: item.project_name,
        }));
        setProjects(projectOptions);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error(error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchPrograms = async () => {
    if (!projectIdForProgramApi) {
      setProgramOptions([]);
      return;
    }

    setLoadingProgram(true);
    try {
      const res = await getProgram.callApi();
      if (res?.success) {
        const options = (res.data || []).map((item: any) => ({
          value: item.program_id,
          label: item.program_name,
        }));
        setProgramOptions(options);
      } else {
        setProgramOptions([]);
      }
    } catch (error) {
      console.error(error);
      setProgramOptions([]);
    } finally {
      setLoadingProgram(false);
    }
  };
  const fetchMonthsReport = async () => {
    try {
      const hasSelection =
        selectedProjectIds.length > 0 || selectedProgramIds.length > 0;
      const payload = hasSelection
        ? {
            project_id: selectedProjectIds,
            program_id: selectedProgramIds,
          }
        : {};

      const res = await getMonthsReport.callApi(payload);

      if (res?.success) {
        setClosedCaseList(res.data ?? []);
      } else {
        setClosedCaseList([]);
      }
    } catch (err) {
      console.error(err);
      setClosedCaseList([]);
    }
  };

  useEffect(() => {
    fetchMonthsReport();
  }, [selectedProjectIds, selectedProgramIds]);

  useEffect(() => {
    fetchProjects();
  }, [orgId]);

  useEffect(() => {
    fetchPrograms();
  }, [projectIdForProgramApi]);

  const columns_closedCases = [
    {
      title: "Months",
      dataIndex: "month",
      key: "month",
      onCell: () => cellWidth(180),
      render: (text: string) => (
        <div className="rb-month-cell">
          <i className="ti ti-calendar-event" />
          <span>{dayjs(text, "YYYY-MM").format("MMMM-YYYY")}</span>
        </div>
      ),
    },
    {
      title: "Applicant Invited",
      dataIndex: "applicant_invited",
      key: "applicant_invited",
      align: "center",
      render: (text: string | number) => (
        <span className="rb-value-strong">{text}</span>
      ),
    },
    {
      title: "Applicant Invite Pending",
      dataIndex: "applicant_invite_pending",
      key: "applicant_invite_pending",
      align: "center",
      render: (text: string | number) => (
        <span className="rb-value-strong">{text}</span>
      ),
      onCell: () => cellWidth(120),
    },
    {
      title: "Invite Expired",
      dataIndex: "invite_expired",
      key: "invite_expired",
      align: "center",
      render: (text: string | number) => (
        <span className="rb-value-strong">{text}</span>
      ),
    },
    {
      title: "WIP",
      dataIndex: "wip",
      key: "wip",
      onCell: () => cellWidth(150),
      align: "center",
      render: (text: string | number) => (
        <span className="rb-value-strong">{text}</span>
      ),
    },
    {
      title: "Client/Candidate Pendency",
      dataIndex: "insufficiency",
      key: "insufficiency",
      align: "center",
      render: (text: string | number) => (
        <span className="rb-value-strong">{text}</span>
      ),
    },
    {
      title: "Total Closed Cases",
      dataIndex: "total_closed_cases",
      key: "total_closed_cases",
      align: "center",
      render: (text: string | number) => (
        <span className="rb-value-strong">{text}</span>
      ),
    },
    {
      title: "Within TAT",
      dataIndex: "within_tat",
      key: "within_tat",
      align: "center",
    },
    {
      title: "Beyond TAT",
      dataIndex: "beyond_tat",
      key: "beyond_tat",
      align: "center",
    },
    {
      title: "SLA %",
      dataIndex: "sla_percentage",
      key: "sla_percentage",
      align: "center",
      onCell: () => cellWidth(90),
      render: (text: string) => <span className="rb-value-strong">{text}</span>,
    },
    // {
    //   title: "Recheck Cases",
    //   dataIndex: "recheckCases",
    //   key: "recheckCases",
    //   align: "center",
    // },
  ];
 
  const columns_ApplicantPending: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "srNo",
      width: 80,
      align: "center",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Applicant Name",
      dataIndex: "applicantName",
      key: "applicantName",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        a.applicantName.localeCompare(b.applicantName),
    },
    {
      title: "Case ID",
      dataIndex: "caseId",
      key: "caseId",
      sorter: (a: any, b: any) => parseNumber(a.caseId) - parseNumber(b.caseId),
    },
    {
      title: "Case SPOC",
      dataIndex: "caseSpoc",
      key: "caseSpoc",
      onCell: () => cellWidth(140),
      sorter: (a: any, b: any) => a.caseSpoc.localeCompare(b.caseSpoc),
    },
    {
      title: "Candidate ID",
      dataIndex: "employeeId",
      key: "employeeId",
      sorter: (a: any, b: any) =>
        parseNumber(a.employeeId) - parseNumber(b.employeeId),
    },
    {
      title: "Email ID",
      dataIndex: "emailId",
      key: "emailId",
      sorter: (a: any, b: any) => a.emailId.localeCompare(b.emailId),
    },
    {
      title: "Days Elapsed",
      dataIndex: "daysElapsed",
      key: "daysElapsed",
      sorter: (a: any, b: any) =>
        parseDaysString(a.daysElapsed) - parseDaysString(b.daysElapsed),
    },
    {
      title: "Phone No.",
      dataIndex: "phone",
      key: "phone",
      render: (text: any) => <div>{text || "-"}</div>,
      sorter: (a: any, b: any) => parseNumber(a.phone) - parseNumber(b.phone),
    },
    {
      title: "Invitation Date",
      dataIndex: "invitationDate",
      key: "invitationDate",
      onCell: () => cellWidth(130),
      sorter: (a: any, b: any) =>
        parseDate(a.invitationDate).getTime() -
        parseDate(b.invitationDate).getTime(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      onCell: () => cellWidth(120),
      render: (status: string) => {
        const isPending = status && status?.startsWith("Pending");
        return (
          <span
            className={`status-badge ${isPending ? "status-badge--pending" : "status-badge--success"}`}
          >
            {status}
          </span>
        );
      },
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
    },
    {
      title: "Invitation Expiry Date",
      dataIndex: "inviteExpiryDate",
      key: "inviteExpiryDate",
      onCell: () => cellWidth(180),
      sorter: (a: any, b: any) =>
        parseDate(a.inviteExpiryDate).getTime() -
        parseDate(b.inviteExpiryDate).getTime(),
    },
    {
      title: "Action",
      key: "action",
      onCell: () => cellWidth(130),
      render: () => (
        <>
          <div className="dropdown">
            <button
              type="button"
              className="rb-action-btn dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <EditIcon />
              &nbsp;Modify Invite
            </button>
            <ul className="dropdown-menu ">
              <li>
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => setShowModifyInvite(true)}
                >
                  Fill by HR
                </button>
              </li>
            </ul>
          </div>
        </>
      ),
    },
    {
      title: "View Log",
      key: "viewLog",
      onCell: () => cellWidth(100),
      render: () => (
        <div className="text-center">
          <button
            type="button"
            className="rb-icon-btn"
            onClick={() => setShowTimeline(true)}
          >
            <i className="ti ti-eye "></i>
          </button>
        </div>
      ),
    },
  ];

  const columns_WIP: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "srNo",
      width: 80,
      align: "center",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Applicant Name",
      dataIndex: "applicant_name",
      key: "applicant_name",
      onCell: () => cellWidth(200),
      render: (text) => (
        <div
          className="rb-link-value"
          onClick={() => {
            navigate(all_routes.caseDetails);
          }}
        >
          {text}
        </div>
      ),
      sorter: (a, b) => a.applicant_name.localeCompare(b.applicant_name),
    },
    {
      title: "Case Id",
      dataIndex: "case_id",
      key: "case_id",
      width: 90,
      sorter: (a, b) => a.case_id.localeCompare(b.case_id),
    },
    {
      title: "Case SPOC",
      dataIndex: "case_spoc",
      key: "case_spoc",
      onCell: () => cellWidth(140),
      sorter: (a, b) => a.case_spoc.localeCompare(b.case_spoc),
    },
    {
      title: "Candidate ID",
      dataIndex: "employee_id",
      key: "employee_id",
      onCell: () => cellWidth(160),
      sorter: (a, b) => a.employee_id.localeCompare(b.employee_id),
    },
    {
      title: "Case Received Date",
      dataIndex: "case_received_date",
      key: "case_received_date",
      onCell: () => cellWidth(150),
      sorter: (a, b) =>
        new Date(a.case_received_date).getTime() -
        new Date(b.case_received_date).getTime(),
    },
    {
      title: "SLA Start Date",
      dataIndex: "sla_start_date",
      key: "sla_start_date",
      onCell: () => cellWidth(150),
      sorter: (a, b) =>
        new Date(a.sla_start_date).getTime() -
        new Date(b.sla_start_date).getTime(),
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      onCell: () => cellWidth(140),
      sorter: (a, b) =>
        new Date(a.due_date).getTime() - new Date(b.due_date).getTime(),
    },
    {
      title: "Total Checks",
      dataIndex: "total_checks",
      key: "total_checks",
      align: "center",
      sorter: (a, b) => a.total_checks - b.total_checks,
    },
    {
      title: "Work In Progress",
      dataIndex: "work_in_progress",
      key: "work_in_progress",
      align: "center",
      sorter: (a, b) => a.work_in_progress - b.work_in_progress,
    },
    {
      title: "Completed",
      dataIndex: "completed",
      key: "completed",
      align: "center",
      sorter: (a, b) => a.completed - b.completed,
    },
    {
      title: "Stop",
      dataIndex: "stop",
      key: "stop",
      align: "center",
      sorter: (a, b) => a.stop - b.stop,
    },
    {
      title: "Actions",
      dataIndex: "",
      onCell: () => cellWidth(170),
      render: () => (
        <>
          <CommonSelect2 className="select " options={actionOptions} />
        </>
      ),
    },
    {
      title: "View Log",
      dataIndex: "",
      onCell: () => cellWidth(100),
      render: () => (
        <div className="text-center">
          <button
            type="button"
            className="rb-icon-btn"
            onClick={() => setShowTimeline(true)}
          >
            <i className="ti ti-eye fs-17"></i>
          </button>
        </div>
      ),
    },
  ];

  const TableData: Record<string, any> = {
    wip: { column: columns_WIP, data: data_WIP },
    applicantInvitePending: {
      column: columns_ApplicantPending,
      data: data_applicantPending,
    },
    clientCandidatePendency: {
      column: columns_WIP,
      data: data_candidatePendency,
    },
  };
  const DynamicTableSelect =
    TableData[selectedTableData?.type as string] || null;
  const handleProjectMultiSelectChange = (values: string[]) => {
    setCustomerScoreData((prev) => ({ ...prev, program: values, sbu: [] }));
  };

  const handleProgramMultiSelectChange = (values: string[]) => {
    setCustomerScoreData((prev) => ({ ...prev, sbu: values }));
  };

  return (
    <div className="rb-module"> 
      <div className="rb-filter-card">
        <div className="rb-filter-card__head">
          <span className="rb-filter-card__icon">
            <i className="ti ti-filter" />
          </span>
          <div>
            <p className="rb-filter-card__title">Filters</p>
            <p className="rb-filter-card__subtitle">
              Narrow the report by program and business unit
            </p>
          </div>
        </div>
        <div className="row gx-3">
          <div className="col-6 col-sm-4 col-lg-3 rb-select-group">
            <MultiSearchableSelect
              label="Program Assignment"
              name="program"
              value={customerScoreData.program}
              options={projects}
              loading={loadingProjects}
              onChange={(_name, values) =>
                handleProjectMultiSelectChange(values)
              }
            />
          </div>
          <div className="col-6 col-sm-4 col-lg-3 rb-select-group">
            <MultiSearchableSelect
              label="Business Unit"
              name="sbu"
              value={customerScoreData.sbu}
              options={programOptions}
              loading={loadingProgram}
              onChange={(_name, values) =>
                handleProgramMultiSelectChange(values)
              }
            />
          </div>
        </div>
      </div>
      <div className="rb-table-card mt-3">
        <div className="rb-table-card__head">
          <div>
            <p className="rb-table-card__title">Monthly Case Summary</p>
            <p className="rb-table-card__subtitle">
              Closed case breakdown and SLA performance by month
            </p>
          </div>
        </div>
        <div className="rb-table-wrap">
          <Datatable
            dataSource={closedCaseList}
            columns={columns_closedCases}
            Selection={false}
            isBordered={true}
            hideSizeChanger={true}
          />
        </div>
      </div>

      {/* Table  */}
      {selectedTableData && DynamicTableSelect && (
        <div className="rb-table-card mt-3">
          <div className="rb-table-card__head">
            <p className="rb-table-card__title">Detail View</p>
          </div>
          <div className="rb-table-wrap">
            <Datatable
              dataSource={DynamicTableSelect.data}
              columns={DynamicTableSelect.column}
              Selection={false}
            />
          </div>
        </div>
      )}

      {showTimeline && <TimelineModal onClose={() => setShowTimeline(false)} />}

      {showModifyInvite && (
        <ModifyInvite
          handleClose={() => setShowModifyInvite(false)}
          handleSubmit={(d) => {
            setShowModifyInvite(false);
          }}
        />
      )}
    </div>
  );
};

export default CustomerScoreCard; 